export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface Size {
  w: number;
  h: number;
}

/** The gap between a card and the panel hung off it, and between a panel and the table's bottom. */
export const HANG_GAP = 12;
export const LIST_WIDTH = 290;
export const DETAILS_WIDTH = 310;

/**
 * Where a panel hangs off a card, in the table's own box: [HANG_GAP] to the card's right with
 * their tops aligned; with no room there, the same distance to its left; with no room below,
 * moved up until its bottom is [HANG_GAP] above the table's bottom. It never lands outside the
 * table. A note hangs [above] the table's top instead of at the card's.
 */
export function hangBeside(card: Rect, panel: Size, table: Size, above = 0): { left: number; top: number } {
  let left = card.right + HANG_GAP;
  if (left + panel.w > table.w) left = card.left - HANG_GAP - panel.w;
  left = Math.max(0, Math.min(left, table.w - panel.w));
  const floor = above ? -above : 0;
  const top = Math.max(floor, Math.min(above ? floor : card.top, table.h - HANG_GAP - panel.h));
  return { left, top };
}

/** How far under the bar a list hangs. */
const UNDER_BAR = 6;

/**
 * Where a list hangs off its bar word, in the page under the head: its left edge 8 left of the
 * word, 6 under the bar, never past the page's right edge. It is only as tall as its content, up to
 * the page's bottom less [HANG_GAP]. [page] is the page's own size, below the head.
 */
export function hangUnder(word: { left: number }, page: Size, width = LIST_WIDTH) {
  return {
    left: Math.max(8, Math.min(word.left - 8, page.w - width - 8)),
    top: UNDER_BAR,
    maxHeight: Math.max(0, page.h - UNDER_BAR - HANG_GAP),
  };
}

/** The same for a panel that hangs off a word at the bar's right end: its right edge under the word's. */
export function hangUnderRight(word: { right: number }, page: Size, width: number) {
  return hangUnder({ left: word.right - width + 8 }, page, width);
}

/** A panel standing against the right of something at [edge] in the page under the head, its
 *  right edge [HANG_GAP] short of it and as wide as its content. */
export function hangLeftOf(edge: number, page: Size) {
  return { right: Math.max(8, page.w - edge + HANG_GAP), top: UNDER_BAR, maxHeight: Math.max(0, page.h - UNDER_BAR - HANG_GAP) };
}
